import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FiClock,
  FiTool,
  FiBox,
  FiTarget,
  FiDroplet,
  FiAlertCircle,
  FiSend,
  FiTruck,
  FiAlertTriangle,
  FiUserCheck,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../../config";
import PageHeader from "../../components/PageHeader/PageHeader";
import LoadingOverlay from "../../components/LoadingOverlay/LoadingOverlay";
import styles from "./ProducaoDiaria.module.css";

const TIPOS_COLHEITA = [
  { value: "Primeiro Desbaste", label: "Primeiro Desbaste" },
  { value: "Segundo Desbaste", label: "Segundo Desbaste" },
  { value: "Corte Raso", label: "Corte Raso" },
  {value: "Triturando", label: "Triturando"},
  {value: "Escarrificando", label: "Escarrificando"}
];

const InputField = React.memo(
  ({
    name,
    label,
    type = "text",
    value,
    placeholder,
    readOnly = false,
    onChange,
    className,
  }) => (
    <div className={styles.inputGroup}>
      <label htmlFor={name}>{label}</label>
      <input
        type={type}
        id={name}
        name={name}
        value={value ?? ""}
        placeholder={placeholder}
        readOnly={readOnly}
        onChange={onChange}
        className={readOnly ? styles.readOnlyInput : className}
      />
    </div>
  )
);

const ProducaoDiaria = () => {
  const navigate = useNavigate();

  const [veiculos, setVeiculos] = useState([]);
  const [veiculoSelecionado, setVeiculoSelecionado] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialFormData = useCallback(
    () => ({
      data: (() => {
        const agora = new Date();
        const ano = agora.getFullYear();
        const mes = String(agora.getMonth() + 1).padStart(2, "0");
        const dia = String(agora.getDate()).padStart(2, "0");
        return `${ano}-${mes}-${dia}`;
      })(),
      horimetro_inicial: "",
      horimetro_final: "",
      motivo_parada: "",
      horas_paradas: "",
      tipo_colheita: null,
      local_colheita: "",
      talhao_colheita: "",
      empresa_contratante: "",
      diesel_litros: "",
      hidraulico_litros: "",
      corrente_litros: "",
      transmissao_litros: "",
      motor_litros: "",
      producao_diaria_valor: "",
      num_arvores_cortadas: "",
      horas_puxar_caminhao: "",
      horas_puxar_madeira: "",
      qtde_julieta_bitren: "",
      qtde_truck: "",
      nota_relevante: "",
    }),
    []
  );

  const [formData, setFormData] = useState(initialFormData);

  const fetchVeiculos = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/api/veiculos/operador`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setVeiculos(response.data);
      setFetchError(null);

      if (response.data.length === 1) {
        const selected = response.data[0];
        setVeiculoSelecionado(selected);
        const tipoDoVeiculo = selected.tipo_colheita;
        if (tipoDoVeiculo) {
          const tipoOpt = TIPOS_COLHEITA.find(
            (opt) => opt.value === tipoDoVeiculo
          );
          setFormData((prev) => ({ ...prev, tipo_colheita: tipoOpt || null }));
        }
      }
    } catch (error) {
      console.error("Erro ao buscar veículos do operador:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Erro ao carregar seus veículos vinculados.";
      setFetchError(errorMessage);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVeiculos();
  }, [fetchVeiculos]);

  useEffect(() => {
    const resetData = initialFormData();
    if (veiculoSelecionado && veiculoSelecionado.tipo_colheita) {
      const tipoOpt = TIPOS_COLHEITA.find(
        (opt) => opt.value === veiculoSelecionado.tipo_colheita
      );
      resetData.tipo_colheita = tipoOpt || null;
    }
    setFormData(resetData);
  }, [veiculoSelecionado, initialFormData]);

  const handleChange = useCallback((e) => {
    const { name, value, type } = e.target;
    let newValue = value;

    if (type === "number") {
      newValue = newValue.replace(",", ".");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  }, []);

  const horasTrabalhadas = useMemo(() => {
    const final = parseFloat(formData.horimetro_final) || 0;
    const inicial = parseFloat(formData.horimetro_inicial) || 0;
    const calc = final - inicial;
    return (calc > 0 ? calc : 0).toFixed(2);
  }, [formData.horimetro_final, formData.horimetro_inicial]);

  const isFormValid = useMemo(() => {
    const hFim = parseFloat(formData.horimetro_final);
    const hInicio = parseFloat(formData.horimetro_inicial);

    const baseValid =
      !!veiculoSelecionado && !!formData.data && !!formData.tipo_colheita;

    const horimetroValid =
      (!formData.horimetro_inicial && !formData.horimetro_final) ||
      hFim >= hInicio;

    return baseValid && horimetroValid;
  }, [veiculoSelecionado, formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid || isSubmitting) {
      Swal.fire(
        "Atenção",
        "Verifique se todos os campos obrigatórios estão preenchidos e se o horímetro final é maior que o inicial.",
        "warning"
      );
      return;
    }

    setIsSubmitting(true);

    const veiculoTipoColheita =
      veiculoSelecionado.tipo_colheita?.toUpperCase() || "";
    const isHarvester = veiculoTipoColheita.includes("HARVESTER");
    const isSkidder = veiculoTipoColheita.includes("SKIDDER");
    const isForwarderOrTratorGuincho =
      veiculoTipoColheita.includes("FORWARDER") ||
      veiculoTipoColheita.includes("TRATOR GUINCHO");
    const isTratorComum = veiculoTipoColheita === "TRATOR";
    const isSemiReboque =
      veiculoSelecionado.tipoVeiculo?.toUpperCase() === "SEMIRREBOQUE";

    const checkValue = (value) => {
      const numericValue = parseFloat(value);
      if (
        value === "" ||
        value === undefined ||
        value === null ||
        isNaN(numericValue)
      ) {
        return null;
      }
      return numericValue;
    };

    const checkStringValue = (value) => {
      if (value === "" || value === undefined || value === null) {
        return null;
      }
      return value;
    };

    const payload = {
      veiculo_id: veiculoSelecionado.id,
      data: formData.data,
      horimetro_inicial: checkValue(formData.horimetro_inicial),
      horimetro_final: checkValue(formData.horimetro_final),
      motivo_parada: checkStringValue(formData.motivo_parada),
      horas_paradas: checkValue(formData.horas_paradas),
      tipo_colheita: formData.tipo_colheita?.value || formData.tipo_colheita,
      local_colheita: checkStringValue(formData.local_colheita),
      talhao_colheita: checkStringValue(formData.talhao_colheita),
      empresa_contratante: isTratorComum ? null : checkStringValue(formData.empresa_contratante),
      nota_relevante: checkStringValue(formData.nota_relevante),

      producao_diaria_valor: isTratorComum ? null : checkValue(formData.producao_diaria_valor),

      num_arvores_cortadas: isHarvester
        ? checkValue(formData.num_arvores_cortadas)
        : null,
      horas_puxar_caminhao: isSkidder
        ? checkValue(formData.horas_puxar_caminhao)
        : null,
      horas_puxar_madeira: isSkidder
        ? checkValue(formData.horas_puxar_madeira)
        : null,
      qtde_julieta_bitren: isForwarderOrTratorGuincho
        ? checkValue(formData.qtde_julieta_bitren)
        : null,
      qtde_truck: isForwarderOrTratorGuincho ? checkValue(formData.qtde_truck) : null,

      diesel_litros: isSemiReboque ? null : checkValue(formData.diesel_litros),
      hidraulico_litros: isSemiReboque
        ? null
        : checkValue(formData.hidraulico_litros),

      corrente_litros:
        isHarvester && !isSemiReboque
          ? checkValue(formData.corrente_litros)
          : null,

      transmissao_litros:
        !isHarvester && !isSemiReboque
          ? checkValue(formData.transmissao_litros)
          : null,
      motor_litros:
        !isHarvester && !isSemiReboque
          ? checkValue(formData.motor_litros)
          : null,
    };

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/api/producao/diaria`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: response.data.message,
        confirmButtonColor: "#16a34a",
      }).then(() => {
        navigate("/");
      });
    } catch (error) {
      console.error("Erro ao enviar produção:", error);
      const errorMessage =
        error.response?.data?.error ||
        "Ocorreu um erro ao registrar a produção.";
      Swal.fire("Erro", errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const SelectTipoColheita = ({ value, placeholder }) => (
    <div className={styles.inputGroup} style={{ marginBottom: 0 }}>
      <div className={styles.selectContainer}>
        <select
          name="tipo_colheita"
          value={value ? (typeof value === 'object' ? value.value : value) : ""}
          onChange={(e) => {
            const selectedOption = TIPOS_COLHEITA.find(
              (opt) => opt.value === e.target.value
            );
            setFormData((prev) => ({ ...prev, tipo_colheita: selectedOption || e.target.value }));
          }}
          className={styles.selectInputLarge}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {TIPOS_COLHEITA.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderAbastecimentoEspecifico = () => {
    if (!veiculoSelecionado) return null;

    const tipoUpper = veiculoSelecionado?.tipo_colheita?.toUpperCase() || "";
    const isHarvester = tipoUpper.includes("HARVESTER");
    const isSemiReboque =
      veiculoSelecionado.tipoVeiculo?.toUpperCase() === "SEMIRREBOQUE";

    if (isSemiReboque)
      return (
        <div
          className={styles.inputGroup}
          style={{
            gridColumn: "1 / -1",
            textAlign: "center",
            color: "#64748b",
          }}
        >
          Não aplicável para **Semi-Reboque**.
        </div>
      );

    return (
      <>
        <InputField
          label="Óleo Diesel (Litros)"
          name="diesel_litros"
          type="number"
          value={formData.diesel_litros}
          placeholder="Ex: 250.5"
          onChange={handleChange}
        />
        <InputField
          label="Óleo Hidráulico (Litros)"
          name="hidraulico_litros"
          type="number"
          value={formData.hidraulico_litros}
          placeholder="Ex: 20.0"
          onChange={handleChange}
        />

        {isHarvester && (
          <InputField
            label="Óleo Corrente (Litros)"
            name="corrente_litros"
            type="number"
            value={formData.corrente_litros}
            placeholder="Ex: 5.0"
            onChange={handleChange}
          />
        )}

        {!isHarvester && (
          <>
            <InputField
              label="Óleo Transmissão (Litros)"
              name="transmissao_litros"
              type="number"
              value={formData.transmissao_litros}
              placeholder="Ex: 10.0"
              onChange={handleChange}
            />
            <InputField
              label="Óleo de Motor (Litros)"
              name="motor_litros"
              type="number"
              value={formData.motor_litros}
              placeholder="Ex: 20.0"
              onChange={handleChange}
            />
          </>
        )}
      </>
    );
  };

  const renderCamposEspecificos = () => {
    if (!veiculoSelecionado) return null;

    const tipoUpper = veiculoSelecionado?.tipo_colheita?.toUpperCase() || "";

    const isHarvester = tipoUpper.includes("HARVESTER");
    const isSkidder = tipoUpper.includes("SKIDDER");
    const isForwarderOrTratorGuincho =
      tipoUpper.includes("FORWARDER") || tipoUpper.includes("TRATOR GUINCHO");
    const isTratorComum = tipoUpper === "TRATOR";

    if (isTratorComum) {
        return null;
    }

    if (isHarvester) {
      return (
        <>
          <div className={styles.sectionTitle}>
            <FiBox /> Produção e Operação (Harvester)
          </div>
          <div className={styles.inputGrid}>
            <InputField
              label="Produção Diária (m³)"
              name="producao_diaria_valor"
              type="number"
              value={formData.producao_diaria_valor}
              placeholder="Ex: 150.75"
              onChange={handleChange}
            />
            <InputField
              label="Número de árvores cortadas"
              name="num_arvores_cortadas"
              type="number"
              value={formData.num_arvores_cortadas}
              placeholder="Ex: 850"
              onChange={handleChange}
            />
          </div>
        </>
      );
    }

    if (isSkidder) {
      return (
        <>
          <div className={styles.sectionTitle}>
            <FiBox /> Produção e Operação (Skidder)
          </div>
          <div className={styles.inputGrid}>
            <InputField
              label="Produção Diária (Qtde genérica/M³)"
              name="producao_diaria_valor"
              type="number"
              value={formData.producao_diaria_valor}
              placeholder="Ex: 120"
              onChange={handleChange}
            />
            <InputField
              label="Quant. Horas Puxar Caminhão"
              name="horas_puxar_caminhao"
              type="number"
              value={formData.horas_puxar_caminhao}
              placeholder="Ex: 4.5"
              onChange={handleChange}
            />
            <InputField
              label="Quant. Horas Puxar Madeira"
              name="horas_puxar_madeira"
              type="number"
              value={formData.horas_puxar_madeira}
              placeholder="Ex: 2.5"
              onChange={handleChange}
            />
          </div>
        </>
      );
    }

    if (isForwarderOrTratorGuincho) {
      const title = tipoUpper.includes("FORWARDER")
        ? "Cargas Carregadas"
        : "Caminhões Puxados";
      const sectionTitle = tipoUpper.includes("TRATOR GUINCHO")
        ? "Trator Guincho"
        : veiculoSelecionado.tipo_colheita;

      return (
        <>
          <div className={styles.sectionTitle}>
            <FiBox /> Produção e Operação ({sectionTitle})
          </div>
          <div className={styles.inputGrid}>
            <InputField
              label={`Produção Diária (Qtde de ${title})`}
              name="producao_diaria_valor"
              type="number"
              value={formData.producao_diaria_valor}
              placeholder="Ex: 10"
              onChange={handleChange}
            />
            <InputField
              label="Quantidade Julietas ou Bitren"
              name="qtde_julieta_bitren"
              type="number"
              value={formData.qtde_julieta_bitren}
              placeholder="Ex: 3"
              onChange={handleChange}
            />
            <InputField
              label="Quantidade de Truck"
              name="qtde_truck"
              type="number"
              value={formData.qtde_truck}
              placeholder="Ex: 7"
              onChange={handleChange}
            />
          </div>
        </>
      );
    }

    return null;
  };

  if (initialLoading) {
    return <LoadingOverlay text="Verificando vínculos..." />;
  }

  if (fetchError || (veiculos.length === 0 && !veiculoSelecionado)) {
    return (
      <div className={styles.container}>
        <PageHeader
          title="Registro de Produção Diária"
          subtitle="Selecione a máquina operada hoje."
          backTo="/"
        />
        <div className={styles.noVehicles}>
          <FiAlertTriangle className={styles.noVehiclesIcon} />
          <h3>
            {fetchError ? "Erro de Vínculo" : "Nenhum Veículo Encontrado"}
          </h3>
          <p>
            {fetchError ||
              "Você não está vinculado a nenhuma máquina ou veículo de colheita. Por favor, contate o administrador."}
          </p>
          <button className={styles.btnCancel} onClick={() => navigate("/")}>
            Voltar para Home
          </button>
        </div>
      </div>
    );
  }

  const showForm = !!veiculoSelecionado;
  const isTrator = veiculoSelecionado?.tipo_colheita?.toUpperCase() === "TRATOR";
  const vehicleType = veiculoSelecionado?.tipoVeiculo || "";
  const vehicleModel = veiculoSelecionado?.modelo || "";

  const isColheitaTypeMissing =
    showForm &&
    veiculoSelecionado.tipoVeiculo !== "SEMIRREBOQUE" &&
    !veiculoSelecionado.tipo_colheita;

  if (isColheitaTypeMissing) {
    return (
      <div className={styles.container}>
        <PageHeader
          title="Registro de Produção Diária"
          subtitle={`Máquina: ${
            veiculoSelecionado.identificacao || veiculoSelecionado.chassi
          } - ${vehicleModel}`}
          backTo="/"
        />
        <div className={styles.missingTypeAlert}>
          <FiAlertTriangle className={styles.alertIcon} />
          <h3>Função de Colheita Não Definida</h3>
          <p>
            O veículo **
            {veiculoSelecionado.identificacao || veiculoSelecionado.chassi} -{" "}
            {veiculoSelecionado.modelo}** não tem uma função de colheita
            (HARVESTER, SKIDDER, etc.) definida no cadastro.
            <br />
            **Acesse o Cadastro de Veículos e defina a função para o veículo{" "}
            {veiculoSelecionado.identificacao || veiculoSelecionado.chassi} para
            prosseguir.**
          </p>
          <button
            className={styles.btnAdminContact}
            onClick={() => navigate("/")}
          >
            Voltar para a Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <PageHeader
        title="Registro de Produção Diária"
        subtitle={`Máquina: ${
          veiculoSelecionado?.identificacao ||
          veiculoSelecionado?.chassi ||
          "Selecione..."
        } - ${vehicleType} ${vehicleModel}`}
        backTo="/"
      />

      <div className={styles.formContainer}>
        {veiculos.length > 1 && (
          <div className={styles.vehicleSelection}>
            <label>Selecione o Veículo/Máquina Operado(a) *</label>
            <select
              value={veiculoSelecionado?.id || ""}
              onChange={(e) => {
                const selectedId = parseInt(e.target.value);
                const selected = veiculos.find((v) => v.id === selectedId);
                setVeiculoSelecionado(selected);
              }}
              className={styles.selectInputLarge}
            >
              <option value="">-- Selecione uma Máquina --</option>
              {veiculos.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.identificacao || v.chassi} - {v.tipoVeiculo} {v.modelo}{" "}
                  {v.tipo_colheita ? `(${v.tipo_colheita})` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        {showForm && (
          <form onSubmit={handleSubmit}>
            <div className={styles.sectionTitle}>
              <FiClock /> Tempo de Uso
            </div>
            <div className={styles.inputGrid}>
              <InputField
                label="Data"
                name="data"
                type="date"
                value={formData.data}
                readOnly
              />
              <InputField
                label="Horímetro Inicial *"
                name="horimetro_inicial"
                type="number"
                value={formData.horimetro_inicial}
                placeholder="Ex: 5432.10"
                onChange={handleChange}
              />
              <InputField
                label="Horímetro Final *"
                name="horimetro_final"
                type="number"
                value={formData.horimetro_final}
                placeholder="Ex: 5440.00"
                onChange={handleChange}
              />
              <InputField
                label="Horas Trabalhadas"
                name="horas_trabalhadas"
                type="text"
                value={horasTrabalhadas}
                readOnly
              />
            </div>

            <div className={styles.sectionTitle}>
              <FiTool /> Parada de Máquina
            </div>
            <div
              className={styles.inputGrid}
              style={{ gridTemplateColumns: "70% 30%" }}
            >
              <InputField
                label="Motivo da Parada (Ex: Troca de Mangueira, Manutenção)"
                name="motivo_parada"
                type="text"
                value={formData.motivo_parada}
                placeholder="Ex: Mangueira do Hidráulico furada"
                onChange={handleChange}
              />
              <InputField
                label="Quantidade de Horas Paradas"
                name="horas_paradas"
                type="number"
                value={formData.horas_paradas}
                placeholder="Ex: 1.5 (Use ponto)"
                onChange={handleChange}
              />
            </div>

            <div className={styles.sectionTitle}>
              <FiTarget /> {isTrator ? "Informações do Serviço" : "Local de Colheita"}
            </div>
            <div className={styles.inputGrid}>
              <div className={styles.inputGroup}>
                <label>{isTrator ? "Tipo de Serviço *" : "Tipo de Colheita *"}</label>
                <SelectTipoColheita
                  value={formData.tipo_colheita}
                  placeholder={isTrator ? "Selecione o Serviço" : "Selecione o Tipo de Serviço"}
                />
              </div>
              <InputField
                label={isTrator ? "Local de Serviço" : "Local de Colheita"}
                name="local_colheita"
                type="text"
                value={formData.local_colheita}
                placeholder="Ex: Fazenda Rio Verde"
                onChange={handleChange}
              />
              <InputField
                label={isTrator ? "Talhão de Serviço" : "Talhão de Colheita"}
                name="talhao_colheita"
                type="text"
                value={formData.talhao_colheita}
                placeholder="Ex: T03"
                onChange={handleChange}
              />
              {!isTrator && (
                <InputField
                  label="Empresa Contratante (Ex: Marchetti)"
                  name="empresa_contratante"
                  type="text"
                  value={formData.empresa_contratante}
                  placeholder="Ex: Marchetti"
                  onChange={handleChange}
                />
              )}
            </div>

            {renderCamposEspecificos()}

            <div className={styles.sectionTitle}>
              <FiDroplet /> Abastecimento
            </div>
            <div className={styles.inputGrid}>
              {renderAbastecimentoEspecifico()}
            </div>

            <div className={styles.sectionTitle}>
              <FiAlertCircle /> Observações do Dia
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="nota_relevante">
                Nota Relevante do Dia (Anomalias, ocorrências ou o que achar
                relevante)
              </label>
              <textarea
                id="nota_relevante"
                name="nota_relevante"
                value={formData.nota_relevante}
                rows="4"
                onChange={handleChange}
                placeholder="Descreva aqui qualquer anormalidade, problema ou informação importante sobre o dia de trabalho."
              />
            </div>

            <div className={styles.footerActions}>
              <button
                type="button"
                className={styles.btnCancel}
                onClick={() => navigate("/")}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={styles.btnSave}
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? (
                  "Registrando..."
                ) : (
                  <>
                    <FiSend /> Registrar Produção
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProducaoDiaria;